/**
 * PosTenderModal — split-tender state machine for the POS till.
 *
 * Flow:
 *  1. creating: POST /api/pos/transactions → get transactionId + totalMinor
 *  2. tendering: operator picks method(s) and pays until outstanding = 0
 *  3. card_confirming: Stripe.js confirmCardPayment in progress
 *  4. completed: show receipt options
 *  5. failed: show error + retry
 *
 * Supported methods: cash | card (manual, Stripe.js) | check | account_credit
 * | gift_card. Card Terminal is rendered disabled ("Available in mobile app").
 */

import { SendPaymentLinkDrawer } from '@/components/SendPaymentLinkDrawer';
import { ApiError, type TipSettings, createPosTransaction, payPosTransaction } from '@/lib/api';
import { getPairedPrinter, kickDrawer, printBytes, renderReceipt } from '@/lib/escpos';
import { formatPriceMinor } from '@/lib/format';
import { loadStripe } from '@stripe/stripe-js';
import {
  CheckSquare,
  CreditCard,
  Gift,
  Minus,
  MonitorSmartphone,
  Smartphone,
  Wallet,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { CartItem } from './PosTillPage';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Phase = 'creating' | 'tendering' | 'card_confirming' | 'completed' | 'failed';

type PaySource = 'cash' | 'stripe_card_manual' | 'check' | 'account_credit' | 'gift_card';

/** Tender tabs: the five ledger sources + the text-to-pay link (migration 0098). */
type TenderMethod = PaySource | 'payment_link';

interface AppliedPayment {
  source: PaySource;
  amountMinor: number;
  chequeNo?: string;
  /** Migration 0098 — gratuity charged on top of a card tender. */
  tipMinor?: number;
}

interface Props {
  cart: CartItem[];
  sessionId: string;
  customerId: string | null;
  bookingId: string | null;
  currency: string;
  facilityName: string;
  /** Tenant business name printed at the top of the receipt. */
  accountName?: string;
  /** Opt-in tax engine — receipt tax line + VAT registration footer. */
  taxLabel?: string | null;
  taxRegistrationNumber?: string | null;
  /** Migration 0098 — per-account tip settings; undefined / disabled = no tip prompt. */
  tipSettings?: TipSettings | null;
  /** First name of the attached customer (for the pay-link copy). */
  customerFirstName?: string | null;
  onComplete: (transactionId: string) => void;
  onCancel: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function methodLabel(source: TenderMethod): string {
  switch (source) {
    case 'payment_link':
      return 'Pay link';
    case 'cash':
      return 'Cash';
    case 'stripe_card_manual':
      return 'Card (keyed)';
    case 'check':
      return 'Cheque';
    case 'account_credit':
      return 'Account credit';
    case 'gift_card':
      return 'Gift card';
  }
}

// ---------------------------------------------------------------------------
// PosTenderModal
// ---------------------------------------------------------------------------

export function PosTenderModal({
  cart,
  sessionId,
  customerId,
  bookingId,
  currency,
  facilityName,
  accountName,
  taxLabel,
  taxRegistrationNumber,
  tipSettings,
  customerFirstName,
  onComplete,
  onCancel,
}: Props) {
  const [phase, setPhase] = useState<Phase>('creating');
  const [transactionId, setTransactionId] = useState<string | null>(null);
  const [totalMinor, setTotalMinor] = useState(0);
  // Migration 0088 — receipt tax breakdown. Populated from the created
  // transaction; both stay 0/null when the tenant has tax disabled.
  const [subtotalMinor, setSubtotalMinor] = useState(0);
  const [taxMinor, setTaxMinor] = useState<number | null>(null);
  const [outstandingMinor, setOutstandingMinor] = useState(0);
  const [payments, setPayments] = useState<AppliedPayment[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Method-specific state
  const [activeMethod, setActiveMethod] = useState<TenderMethod>('cash');
  // Migration 0098 — tip chosen on the card step. Null preset = no tip.
  const [tipPct, setTipPct] = useState<number | 'custom' | null>(null);
  const [customTipInput, setCustomTipInput] = useState('');
  const [payLinkSent, setPayLinkSent] = useState(false);
  const [cashInput, setCashInput] = useState('');
  const [chequeNo, setChequeNo] = useState('');
  const [creditAmountInput, setCreditAmountInput] = useState('');
  const [giftCardCode, setGiftCardCode] = useState('');
  const [busy, setBusy] = useState(false);

  // Stripe card element refs
  const cardMountRef = useRef<HTMLDivElement | null>(null);
  // biome-ignore lint/suspicious/noExplicitAny: Stripe types aren't worth re-typing here
  const stripeRef = useRef<any>(null);
  // biome-ignore lint/suspicious/noExplicitAny: StripeElements isn't worth re-typing here
  const elementsRef = useRef<any>(null);
  const [cardReady, setCardReady] = useState(false);

  // Receipt state after completion
  const [printBusy, setPrintBusy] = useState(false);

  // ---------------------------------------------------------------------------
  // Step 1: create transaction on mount
  // ---------------------------------------------------------------------------

  const createTransaction = useCallback(async () => {
    setPhase('creating');
    setError(null);
    try {
      const res = await createPosTransaction({
        session_id: sessionId,
        customer_id: customerId,
        booking_id: bookingId,
        lines: cart.map((l) => ({
          kind: l.kind,
          pos_product_id: l.posProductId ?? null,
          service_id: l.serviceId ?? null,
          description: l.description,
          quantity: l.quantity,
          unit_price_minor: l.unitPriceMinor,
          taxable: l.taxable,
          position: l.position,
        })),
      });
      setTransactionId(res.transaction.id);
      setTotalMinor(res.transaction.totalMinor);
      setSubtotalMinor(res.transaction.subtotalMinor ?? res.transaction.totalMinor);
      setTaxMinor(res.transaction.taxMinor ?? null);
      setOutstandingMinor(res.transaction.totalMinor);
      setCashInput((res.transaction.totalMinor / 100).toFixed(2));
      setCreditAmountInput((res.transaction.totalMinor / 100).toFixed(2));
      setPhase('tendering');
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `Could not create transaction (${err.code}).`
          : 'Could not create transaction. Please try again.';
      setError(msg);
      setPhase('failed');
    }
  }, [sessionId, customerId, bookingId, cart]);

  useEffect(() => {
    void createTransaction();
  }, [createTransaction]);

  // ---------------------------------------------------------------------------
  // Stripe card element lifecycle
  // ---------------------------------------------------------------------------

  // biome-ignore lint/suspicious/noExplicitAny: import.meta.env is untyped in this tsconfig
  const publishableKey = (import.meta as any).env?.VITE_STRIPE_PUBLISHABLE_KEY as
    | string
    | undefined;

  useEffect(() => {
    if (phase !== 'card_confirming') return;
    if (!publishableKey) {
      // No publishable key in test env — skip Stripe loading but mark ready
      // so the confirm button is not permanently disabled.
      setCardReady(true);
      return;
    }

    let cancelled = false;
    let cardElement: unknown = null;

    async function mountCard() {
      // We need a clientSecret to init elements with a PI. For manual card
      // entry we mount the card element without a PI (will confirm later).
      const stripe = await loadStripe(publishableKey as string);
      if (cancelled || !stripe) return;
      stripeRef.current = stripe;
      const elements = stripe.elements();
      elementsRef.current = elements;
      cardElement = elements.create('card', { hidePostalCode: true });
      // Mount only if the ref node is available; if not, still mark ready
      // so the UI isn't permanently blocked.
      if (cardMountRef.current) {
        // biome-ignore lint/suspicious/noExplicitAny: StripeCardElement.mount
        (cardElement as any).mount(cardMountRef.current);
      }
      if (!cancelled) setCardReady(true);
    }

    void mountCard();

    return () => {
      cancelled = true;
      if (cardElement) {
        try {
          // biome-ignore lint/suspicious/noExplicitAny: StripeCardElement.unmount
          (cardElement as any).unmount();
        } catch {
          // ignore
        }
      }
      stripeRef.current = null;
      elementsRef.current = null;
      setCardReady(false);
    };
  }, [phase, publishableKey]);

  // ---------------------------------------------------------------------------
  // Pay helpers
  // ---------------------------------------------------------------------------

  async function applyPayment(
    source: PaySource,
    amountMinor: number,
    extra?: { chequeNo?: string; giftCardCode?: string },
  ) {
    if (!transactionId) return;
    setBusy(true);
    setError(null);
    try {
      const res = await payPosTransaction(transactionId, {
        source,
        amount_minor: amountMinor,
        ...(extra?.chequeNo ? { cheque_no: extra.chequeNo } : {}),
        ...(extra?.giftCardCode ? { gift_card_code: extra.giftCardCode } : {}),
      });

      // The server may clamp the applied amount (a gift card with less
      // remaining balance than the outstanding total applies what it holds).
      // Trust the returned payment row, not the requested amount.
      const appliedMinor = res.payment?.amountMinor ?? amountMinor;
      const applied: AppliedPayment = {
        source,
        amountMinor: appliedMinor,
        ...(extra?.chequeNo ? { chequeNo: extra.chequeNo } : {}),
      };
      setPayments((prev) => [...prev, applied]);
      if (source === 'gift_card') setGiftCardCode('');

      if (res.transaction_closed) {
        setOutstandingMinor(0);
        setPhase('completed');
      } else {
        const newOutstanding = outstandingMinor - appliedMinor;
        setOutstandingMinor(newOutstanding);
        const remaining = (Math.max(0, newOutstanding) / 100).toFixed(2);
        setCashInput(remaining);
        setCreditAmountInput(remaining);
      }
    } catch (err) {
      const code = err instanceof ApiError ? err.code : null;
      if (code === 'insufficient_credit') {
        setError('Insufficient account credit to cover this amount.');
      } else if (code === 'gift_card_not_found') {
        setError('Gift card not found. Check the code and try again.');
      } else if (code === 'gift_card_expired') {
        setError('This gift card has expired.');
      } else if (code === 'gift_card_not_active') {
        setError('This gift card is no longer active.');
      } else if (code === 'gift_card_no_balance' || code === 'gift_card_insufficient_balance') {
        setError('This gift card has no remaining balance.');
      } else if (code === 'gift_card_requires_customer') {
        setError('Attach a customer to redeem a gift card.');
      } else if (err instanceof ApiError) {
        setError(`Payment failed (${err.code}). Please try again.`);
      } else {
        setError('Payment failed. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleCardConfirm() {
    if (!transactionId) return;
    setBusy(true);
    setError(null);

    try {
      let paymentMethodId: string | undefined;

      if (stripeRef.current && elementsRef.current) {
        // Create a PaymentMethod from the card element
        const { paymentMethod, error: pmError } = await stripeRef.current.createPaymentMethod({
          type: 'card',
          card: elementsRef.current.getElement('card'),
        });

        if (pmError || !paymentMethod) {
          setError(pmError?.message ?? 'Could not create payment method.');
          setBusy(false);
          return;
        }
        paymentMethodId = paymentMethod.id;
      }

      // Call our API to create a PaymentIntent and pay (amount + tip).
      const res = await payPosTransaction(transactionId, {
        source: 'stripe_card_manual',
        amount_minor: outstandingMinor,
        ...(paymentMethodId ? { stripe_payment_method_id: paymentMethodId } : {}),
        ...(tipMinor > 0 ? { tip_minor: tipMinor } : {}),
      });

      if (res.payment_intent?.client_secret && stripeRef.current) {
        // Server returned a client secret — confirm the payment intent
        const { error: confirmError, paymentIntent } = await stripeRef.current.confirmCardPayment(
          res.payment_intent.client_secret,
          {
            payment_method: paymentMethodId,
          },
        );

        if (confirmError) {
          setError(confirmError.message ?? 'Card payment failed.');
          setBusy(false);
          return;
        }

        if (paymentIntent?.status !== 'succeeded') {
          setError('Card payment did not succeed. Please try again.');
          setBusy(false);
          return;
        }
      }

      const applied: AppliedPayment = {
        source: 'stripe_card_manual',
        amountMinor: outstandingMinor,
        ...(tipMinor > 0 ? { tipMinor } : {}),
      };
      setPayments((prev) => [...prev, applied]);

      if (res.transaction_closed) {
        setOutstandingMinor(0);
        setPhase('completed');
      } else {
        setOutstandingMinor(0);
        setPhase('tendering');
        setActiveMethod('cash');
      }
    } catch (err) {
      const msg =
        err instanceof ApiError ? `Payment failed (${err.code}).` : 'Card payment failed.';
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Print receipt
  // ---------------------------------------------------------------------------

  async function handlePrint() {
    setPrintBusy(true);
    try {
      const printer = await getPairedPrinter();
      if (!printer) {
        setError('No printer paired. Pair a printer in Settings → Printer.');
        return;
      }
      const hasCash = payments.some((p) => p.source === 'cash');
      const bytes = renderReceipt({
        account: {
          name: accountName ?? facilityName,
          taxLabel: taxLabel ?? null,
          taxRegistrationNumber: taxRegistrationNumber ?? null,
        },
        facility: { name: facilityName, addressLine: '' },
        transaction: {
          id: transactionId ?? '',
          total_minor: totalMinor,
          tax_minor: taxMinor,
          subtotal_minor: subtotalMinor,
          currency,
        },
        lines: cart.map((l) => ({
          description: l.description,
          quantity: l.quantity,
          unit_price_minor: l.unitPriceMinor,
          line_total_minor: l.quantity * l.unitPriceMinor,
        })),
        payments: payments.map((p) => ({ source: p.source, amount_minor: p.amountMinor })),
        staffName: '',
        printedAt: new Date().toISOString(),
      });
      await printBytes(bytes);
      if (hasCash) {
        await kickDrawer();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Print failed.');
    } finally {
      setPrintBusy(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Cash change calculation
  // ---------------------------------------------------------------------------

  const cashMinor = Math.round(Number.parseFloat(cashInput || '0') * 100);
  const changeMinor = cashMinor - outstandingMinor;

  // ---------------------------------------------------------------------------
  // Tip (migration 0098) — card tenders only
  // ---------------------------------------------------------------------------

  const tipsEnabled = Boolean(tipSettings?.tipsEnabled);
  const tipMinor = !tipsEnabled
    ? 0
    : tipPct === 'custom'
      ? Math.max(0, Math.round(Number.parseFloat(customTipInput || '0') * 100) || 0)
      : tipPct !== null
        ? Math.round((outstandingMinor * tipPct) / 100)
        : 0;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center sm:items-center"
      aria-modal="true"
      role="dialog"
      aria-label="Tender"
    >
      <div
        className="absolute inset-0 bg-background/70 backdrop-blur-sm"
        onClick={phase === 'tendering' ? onCancel : undefined}
      />
      <div className="relative w-full max-w-md rounded-t-2xl sm:rounded-2xl bg-surface shadow-2xl ring-1 ring-border">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div>
            <h2 className="font-heading text-base font-medium">Tender</h2>
            {phase === 'tendering' && (
              <p className="mt-0.5 text-sm text-muted-foreground">
                Outstanding:{' '}
                <span className="num font-medium text-foreground">
                  {formatPriceMinor(outstandingMinor, currency)}
                </span>
              </p>
            )}
          </div>
          {(phase === 'tendering' || phase === 'failed') && (
            <button
              type="button"
              onClick={onCancel}
              className="rounded-md p-1.5 text-muted-foreground hover:bg-secondary hover:text-foreground press"
              aria-label="Cancel tender"
            >
              <X className="size-4" strokeWidth={1.75} />
            </button>
          )}
        </div>

        {/* Body */}
        <div className="p-6">
          {/* Creating / loading */}
          {phase === 'creating' && (
            <div className="flex items-center justify-center py-8">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              <span className="ml-3 text-sm text-muted-foreground">Preparing transaction…</span>
            </div>
          )}

          {/* Tendering */}
          {phase === 'tendering' && (
            <div className="space-y-5">
              {/* Payments applied so far */}
              {payments.length > 0 && (
                <div className="rounded-lg bg-secondary/60 px-3 py-2 space-y-1">
                  <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground mb-2">
                    Payments applied
                  </p>
                  {payments.map((p, i) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: ordered list
                    <div key={i} className="text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{methodLabel(p.source)}</span>
                        <span className="num font-medium">
                          {formatPriceMinor(p.amountMinor, currency)}
                        </span>
                      </div>
                      {p.tipMinor !== undefined && p.tipMinor > 0 && (
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>Tip</span>
                          <span className="num">{formatPriceMinor(p.tipMinor, currency)}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Method tabs */}
              <div className="flex gap-1.5 flex-wrap">
                {[
                  { source: 'cash' as PaySource, label: 'Cash', Icon: Wallet },
                  { source: 'stripe_card_manual' as PaySource, label: 'Card', Icon: CreditCard },
                  { source: 'check' as PaySource, label: 'Cheque', Icon: CheckSquare },
                  ...(customerId !== null
                    ? [
                        { source: 'account_credit' as TenderMethod, label: 'Credit', Icon: Minus },
                        { source: 'gift_card' as TenderMethod, label: 'Gift card', Icon: Gift },
                      ]
                    : []),
                  { source: 'payment_link' as TenderMethod, label: 'Pay link', Icon: Smartphone },
                ].map(({ source, label, Icon }) => (
                  <button
                    key={source}
                    type="button"
                    onClick={() => {
                      setActiveMethod(source);
                      if (source === 'stripe_card_manual') setPhase('card_confirming');
                    }}
                    className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm press transition-colors ${
                      activeMethod === source && phase === 'tendering'
                        ? 'border-primary bg-primary/8 text-primary font-medium'
                        : 'border-border text-muted-foreground hover:border-border/80 hover:text-foreground'
                    }`}
                    data-testid={`method-${source}`}
                  >
                    <Icon className="size-3.5" strokeWidth={1.75} />
                    {label}
                  </button>
                ))}

                {/* Terminal — disabled */}
                <button
                  type="button"
                  disabled
                  title="Available in mobile app"
                  className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm text-muted-foreground opacity-40 cursor-not-allowed"
                  data-testid="method-terminal"
                >
                  <MonitorSmartphone className="size-3.5" strokeWidth={1.75} />
                  Terminal
                </button>
              </div>

              {/* Cash form */}
              {activeMethod === 'cash' && (
                <div className="space-y-3">
                  <div>
                    <label
                      htmlFor="cash-amount"
                      className="mb-1 block text-xs font-medium text-muted-foreground"
                    >
                      Cash tendered
                    </label>
                    <div className="flex items-center rounded-lg border border-border bg-background px-3">
                      <span className="mr-1 text-sm text-muted-foreground">£</span>
                      <input
                        id="cash-amount"
                        type="number"
                        min="0"
                        step="0.01"
                        value={cashInput}
                        onChange={(e) => setCashInput(e.target.value)}
                        className="w-full bg-transparent py-2.5 text-sm num outline-none"
                        data-testid="cash-input"
                      />
                    </div>
                  </div>
                  {cashMinor > outstandingMinor && (
                    <div className="flex justify-between rounded-lg bg-success/10 px-3 py-2 text-sm">
                      <span className="text-muted-foreground">Change</span>
                      <span className="num font-medium text-success">
                        {formatPriceMinor(changeMinor, currency)}
                      </span>
                    </div>
                  )}
                  <button
                    type="button"
                    disabled={busy || cashMinor <= 0}
                    onClick={() => void applyPayment('cash', Math.min(cashMinor, outstandingMinor))}
                    className="w-full rounded-lg bg-primary py-2.5 text-sm font-medium text-primary-foreground press disabled:opacity-50"
                    data-testid="apply-cash-btn"
                  >
                    {busy ? 'Processing…' : 'Apply cash'}
                  </button>
                </div>
              )}

              {/* Cheque form */}
              {activeMethod === 'check' && (
                <div className="space-y-3">
                  <div>
                    <label
                      htmlFor="cheque-no"
                      className="mb-1 block text-xs font-medium text-muted-foreground"
                    >
                      Cheque number
                    </label>
                    <input
                      id="cheque-no"
                      type="text"
                      value={chequeNo}
                      onChange={(e) => setChequeNo(e.target.value)}
                      placeholder="e.g. 001234"
                      className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm outline-none"
                      data-testid="cheque-no-input"
                    />
                  </div>
                  <button
                    type="button"
                    disabled={busy || !chequeNo.trim()}
                    onClick={() =>
                      void applyPayment('check', outstandingMinor, { chequeNo: chequeNo.trim() })
                    }
                    className="w-full rounded-lg bg-primary py-2.5 text-sm font-medium text-primary-foreground press disabled:opacity-50"
                    data-testid="apply-check-btn"
                  >
                    {busy
                      ? 'Processing…'
                      : `Apply cheque ${formatPriceMinor(outstandingMinor, currency)}`}
                  </button>
                </div>
              )}

              {/* Account credit form */}
              {activeMethod === 'account_credit' && customerId !== null && (
                <div className="space-y-3">
                  <div>
                    <label
                      htmlFor="credit-amount"
                      className="mb-1 block text-xs font-medium text-muted-foreground"
                    >
                      Credit amount
                    </label>
                    <div className="flex items-center rounded-lg border border-border bg-background px-3">
                      <span className="mr-1 text-sm text-muted-foreground">£</span>
                      <input
                        id="credit-amount"
                        type="number"
                        min="0"
                        step="0.01"
                        value={creditAmountInput}
                        onChange={(e) => setCreditAmountInput(e.target.value)}
                        className="w-full bg-transparent py-2.5 text-sm num outline-none"
                        data-testid="credit-amount-input"
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      const amt = Math.round(Number.parseFloat(creditAmountInput || '0') * 100);
                      if (amt > 0) void applyPayment('account_credit', amt);
                    }}
                    className="w-full rounded-lg bg-primary py-2.5 text-sm font-medium text-primary-foreground press disabled:opacity-50"
                    data-testid="apply-credit-btn"
                  >
                    {busy ? 'Processing…' : 'Apply credit'}
                  </button>
                </div>
              )}

              {/* Account credit disabled when no customer */}
              {activeMethod === 'account_credit' && customerId === null && (
                <p className="text-sm text-muted-foreground">
                  Attach a customer to use account credit.
                </p>
              )}

              {/* Gift card form */}
              {activeMethod === 'gift_card' && customerId !== null && (
                <div className="space-y-3">
                  <div>
                    <label
                      htmlFor="gift-card-code"
                      className="mb-1 block text-xs font-medium text-muted-foreground"
                    >
                      Gift card code
                    </label>
                    <input
                      id="gift-card-code"
                      type="text"
                      value={giftCardCode}
                      onChange={(e) => setGiftCardCode(e.target.value)}
                      placeholder="e.g. GIFT-A1B2C3D4"
                      autoCapitalize="characters"
                      className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm num outline-none"
                      data-testid="gift-card-code-input"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Applies the card's remaining balance, up to the outstanding total.
                  </p>
                  <button
                    type="button"
                    disabled={busy || !giftCardCode.trim()}
                    onClick={() =>
                      void applyPayment('gift_card', outstandingMinor, {
                        giftCardCode: giftCardCode.trim(),
                      })
                    }
                    className="w-full rounded-lg bg-primary py-2.5 text-sm font-medium text-primary-foreground press disabled:opacity-50"
                    data-testid="apply-gift-card-btn"
                  >
                    {busy ? 'Processing…' : 'Apply gift card'}
                  </button>
                </div>
              )}

              {/* Gift card disabled when no customer */}
              {activeMethod === 'gift_card' && customerId === null && (
                <p className="text-sm text-muted-foreground">
                  Attach a customer to redeem a gift card.
                </p>
              )}

              {/* Text-to-pay link (migration 0098) */}
              {activeMethod === 'payment_link' && customerId === null && (
                <p className="text-sm text-muted-foreground" data-testid="pay-link-needs-customer">
                  Attach a customer to send a payment link.
                </p>
              )}
              {activeMethod === 'payment_link' && customerId !== null && transactionId && (
                <div className="space-y-3">
                  <SendPaymentLinkDrawer
                    variant="inline"
                    open
                    onClose={() => setActiveMethod('cash')}
                    target={{ purpose: 'pos', posTransactionId: transactionId }}
                    defaultAmountMinor={outstandingMinor}
                    allowAmountEdit={false}
                    currency={currency}
                    customerFirstName={customerFirstName ?? 'The customer'}
                    onCreated={() => setPayLinkSent(true)}
                  />
                  {payLinkSent && (
                    <div className="rounded-lg bg-secondary/60 px-3 py-2 text-xs text-muted-foreground">
                      This sale stays open until the customer pays. You can close this window — the
                      till will show it as completed once the payment lands.
                      <button
                        type="button"
                        onClick={onCancel}
                        className="ml-2 font-medium text-primary underline-offset-2 hover:underline"
                        data-testid="pay-link-close-btn"
                      >
                        Close
                      </button>
                    </div>
                  )}
                </div>
              )}

              {error && (
                <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {error}
                </p>
              )}
            </div>
          )}

          {/* Card confirming — Stripe card element */}
          {phase === 'card_confirming' && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Enter card details to charge{' '}
                <span className="num font-medium text-foreground">
                  {formatPriceMinor(outstandingMinor + tipMinor, currency)}
                </span>
                {tipMinor > 0 ? (
                  <>
                    {' '}
                    (includes <span className="num">{formatPriceMinor(tipMinor, currency)}</span>{' '}
                    tip)
                  </>
                ) : (
                  '.'
                )}
              </p>

              {/* Tip prompt (migration 0098) — only when the tenant enabled tips */}
              {tipsEnabled && tipSettings && (
                <fieldset className="space-y-2" data-testid="tip-prompt">
                  <legend className="text-xs font-medium text-muted-foreground">Add a tip?</legend>
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      type="button"
                      aria-pressed={tipPct === null}
                      onClick={() => setTipPct(null)}
                      className={`rounded-full border px-3 py-1 text-sm press ${
                        tipPct === null
                          ? 'border-primary bg-primary/8 text-primary font-medium'
                          : 'border-border text-muted-foreground'
                      }`}
                      data-testid="tip-none"
                    >
                      No tip
                    </button>
                    {tipSettings.tipPresets.map((pct) => (
                      <button
                        key={pct}
                        type="button"
                        aria-pressed={tipPct === pct}
                        onClick={() => setTipPct(pct)}
                        className={`rounded-full border px-3 py-1 text-sm press ${
                          tipPct === pct
                            ? 'border-primary bg-primary/8 text-primary font-medium'
                            : 'border-border text-muted-foreground'
                        }`}
                        data-testid={`tip-${pct}`}
                      >
                        <span className="num">{pct}%</span>{' '}
                        <span className="num opacity-70">
                          ({formatPriceMinor(Math.round((outstandingMinor * pct) / 100), currency)})
                        </span>
                      </button>
                    ))}
                    {tipSettings.allowCustomTip && (
                      <button
                        type="button"
                        aria-pressed={tipPct === 'custom'}
                        onClick={() => setTipPct('custom')}
                        className={`rounded-full border px-3 py-1 text-sm press ${
                          tipPct === 'custom'
                            ? 'border-primary bg-primary/8 text-primary font-medium'
                            : 'border-border text-muted-foreground'
                        }`}
                        data-testid="tip-custom"
                      >
                        Custom
                      </button>
                    )}
                  </div>
                  {tipPct === 'custom' && (
                    <div className="flex items-center rounded-lg border border-border bg-background px-3">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        inputMode="decimal"
                        aria-label="Custom tip amount"
                        value={customTipInput}
                        onChange={(e) => setCustomTipInput(e.target.value)}
                        className="w-full bg-transparent py-2 text-sm num outline-none"
                        data-testid="tip-custom-input"
                      />
                    </div>
                  )}
                </fieldset>
              )}

              <div
                ref={cardMountRef}
                data-testid="stripe-card-element"
                className="rounded-lg border border-border bg-background p-3"
              />

              {error && (
                <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {error}
                </p>
              )}

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setPhase('tendering');
                    setActiveMethod('cash');
                    setError(null);
                  }}
                  className="flex-1 rounded-lg border border-border py-2.5 text-sm press"
                >
                  Back
                </button>
                <button
                  type="button"
                  disabled={busy || !cardReady}
                  onClick={() => void handleCardConfirm()}
                  className="flex-1 rounded-lg bg-primary py-2.5 text-sm font-medium text-primary-foreground press disabled:opacity-50"
                  data-testid="confirm-card-btn"
                >
                  {busy ? 'Processing…' : 'Charge card'}
                </button>
              </div>
            </div>
          )}

          {/* Completed */}
          {phase === 'completed' && (
            <div className="space-y-5">
              <div className="rounded-xl bg-success/10 px-4 py-4 text-center">
                <p className="font-medium text-success">Sale complete</p>
                <p className="mt-1 num text-sm text-muted-foreground">Txn {transactionId}</p>
              </div>

              <div className="space-y-1">
                <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                  Payments
                </p>
                {payments.map((p, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: ordered list
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      {methodLabel(p.source)}
                      {p.chequeNo && <span className="ml-1 num text-xs">#{p.chequeNo}</span>}
                    </span>
                    <span className="num font-medium">
                      {formatPriceMinor(p.amountMinor, currency)}
                    </span>
                  </div>
                ))}
              </div>

              {error && (
                <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {error}
                </p>
              )}

              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={printBusy}
                  onClick={() => void handlePrint()}
                  className="flex-1 rounded-lg border border-border py-2.5 text-sm press disabled:opacity-50"
                  data-testid="print-receipt-btn"
                >
                  {printBusy ? 'Printing…' : 'Print receipt'}
                </button>
                <button
                  type="button"
                  onClick={() => onComplete(transactionId ?? '')}
                  className="flex-1 rounded-lg bg-primary py-2.5 text-sm font-medium text-primary-foreground press"
                  data-testid="done-btn"
                >
                  Done
                </button>
              </div>
            </div>
          )}

          {/* Failed */}
          {phase === 'failed' && (
            <div className="space-y-4">
              <p className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
                {error ?? 'Transaction failed.'}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={onCancel}
                  className="flex-1 rounded-lg border border-border py-2.5 text-sm press"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => void createTransaction()}
                  className="flex-1 rounded-lg bg-primary py-2.5 text-sm font-medium text-primary-foreground press"
                >
                  Retry
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
